// WebinarManagementPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, CalendarDays, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import WebinarForm from "../Components/WebinarForm";

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const WebinarManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchWebinars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(apiUrl.GET_WEBINARS);
      const payload = res.data?.data;
      if (res.data?.status && payload && Array.isArray(payload.webinars)) {
        setWebinars(payload.webinars);
      } else {
        setWebinars([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load webinars");
      setWebinars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = async (webinar) => {
    if (!window.confirm(`Delete "${webinar.topic}"? This cannot be undone.`))
      return;
    setDeletingId(webinar._id);
    try {
      await axiosInstance.delete(apiUrl.DELETE_WEBINAR(webinar._id));
      await fetchWebinars();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to delete webinar";
      alert(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (webinar) => {
    setEditingWebinar(webinar);
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingWebinar(null);
    fetchWebinars();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingWebinar(null);
  };

  useEffect(() => {
    fetchWebinars();
  }, [fetchWebinars]);

  // Show create form
  if (showForm) {
    return (
      <div className="space-y-6">
        <WebinarForm onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  // Show edit form
  if (editingWebinar) {
    return (
      <div className="space-y-6">
        <WebinarForm 
          webinar={editingWebinar}
          onSuccess={handleSave} 
          onCancel={handleCancel} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Events</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Create Event
        </button>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">
          Upcoming & past events
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
            {error}
          </div>
        ) : webinars.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
            <CalendarDays size={36} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No webinars yet</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
            >
              Create your first webinar
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {webinars.map((webinar) => (
              <article
                key={webinar._id}
                className="flex flex-col overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow"
              >
                {webinar.poster && (
                  <div className="h-24 w-full shrink-0 bg-[#13233a]">
                    <img
                      src={webinar.poster}
                      alt={webinar.topic}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  <h4 className="truncate text-sm font-semibold text-slate-100">
                    {webinar.topic}
                  </h4>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {webinar.category || "Uncategorised"}
                  </p>
                  <div className="mt-2 space-y-1.5 text-xs text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <CalendarDays size={12} />
                      <span>
                        {formatDateTime(webinar.startDate)} –{" "}
                        {formatDateTime(webinar.endDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2
                        size={12}
                        className={
                          webinar.isComplete
                            ? "text-emerald-600"
                            : "text-amber-500"
                        }
                      />
                      <span>
                        {webinar.isComplete ? "Completed" : "Upcoming / live"}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1 border-t border-[#17283f] pt-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(webinar)}
                        className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(webinar)}
                        disabled={deletingId === webinar._id}
                        className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        title="Delete"
                      >
                        {deletingId === webinar._id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default WebinarManagementPage;